import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useLang } from "../../lib/i18n";

const STORAGE_KEY = "ssd-consent-v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function writeConsent(c) {
  const val = { ...c, ts: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(val));
  try { window.dispatchEvent(new CustomEvent("consent-changed", { detail: val })); } catch { /* noop */ }
}

export default function CookieBanner({ openSignal }) {
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, statistik: false, marketing: false, externe_medien: false });
  const { t, lp } = useLang();

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setOpen(true);
    else setPrefs({ ...prefs, ...existing });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (openSignal > 0) { setOpen(true); setSettingsOpen(true); }
  }, [openSignal]);

  const acceptAll = () => {
    const p = { necessary: true, statistik: true, marketing: true, externe_medien: true };
    setPrefs(p); writeConsent(p); setOpen(false); setSettingsOpen(false);
  };
  const acceptNecessary = () => {
    const p = { necessary: true, statistik: false, marketing: false, externe_medien: false };
    setPrefs(p); writeConsent(p); setOpen(false); setSettingsOpen(false);
  };
  const saveSelection = () => {
    writeConsent(prefs); setOpen(false); setSettingsOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-5" data-testid="cookie-banner">
      <div className="container-x">
        <div className="bg-white border border-outline rounded-2xl shadow-lg p-5 md:p-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-navy mt-0.5 shrink-0" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-navy-dark mb-1">{t("ckTitle")}</h2>
              <p className="text-sm text-ink-muted leading-relaxed">
                {t("ckBody")}<a href={lp("/datenschutz/")} className="underline text-navy">{t("ckPrivacy")}</a>.
              </p>

              {settingsOpen && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="cookie-settings-grid">
                  {[
                    { k: "necessary", l: t("ckNecessary"), d: t("ckNecessaryDesc"), disabled: true },
                    { k: "statistik", l: t("ckStatistik"), d: t("ckStatistikDesc") },
                    { k: "marketing", l: t("ckMarketing"), d: t("ckMarketingDesc") },
                    { k: "externe_medien", l: t("ckExternal"), d: t("ckExternalDesc") },
                  ].map((c) => (
                    <label key={c.k} className={`flex items-start gap-3 p-3 rounded-lg border ${prefs[c.k] ? "border-navy bg-navy/[0.03]" : "border-outline"} cursor-pointer`}>
                      <input
                        type="checkbox"
                        checked={!!prefs[c.k]}
                        disabled={c.disabled}
                        onChange={(e) => setPrefs({ ...prefs, [c.k]: e.target.checked })}
                        className="mt-1 h-4 w-4 accent-navy"
                        data-testid={`cookie-cat-${c.k}`}
                      />
                      <div>
                        <div className="font-medium text-ink">{c.l}</div>
                        <div className="text-xs text-ink-muted">{c.d}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={acceptAll} className="btn-primary !h-11 !px-5 !text-sm" data-testid="cookie-accept-all">
                  {t("ckAcceptAll")}
                </button>
                <button onClick={acceptNecessary} className="btn-ghost !h-11 !px-5 !text-sm" data-testid="cookie-accept-necessary">
                  {t("ckOnlyNecessary")}
                </button>
                {!settingsOpen ? (
                  <button onClick={() => setSettingsOpen(true)} className="btn-ghost !h-11 !px-5 !text-sm" data-testid="cookie-open-settings">
                    {t("ckSettings")}
                  </button>
                ) : (
                  <button onClick={saveSelection} className="btn-secondary !h-11 !px-5 !text-sm" data-testid="cookie-save">
                    {t("ckSave")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
